import { FC } from "react";
import { Box, Typography } from "@mui/material";
import { ArrowDropDown, ArrowDropUp } from "@mui/icons-material";
import { makeSxStyles } from "ui";
import { Nullable } from "service";
import { SortOptions } from "@/shared/components/table/GenericTable";
import { ORGANIZATIONS_LIST_COLUMNS } from "../constants/organizations-list-columns";

export interface OrganizationsListHeaderProps {
  sortOptions?: Nullable<SortOptions>;
  onSortChange?: (sortOptions: Nullable<SortOptions>) => void;
  // Set to false for single-row contexts (e.g. the Organization Info section
  // on the detail page) where there's only ever one organization to show, so
  // sorting doesn't apply.
  sortable?: boolean;
}

const OrganizationsListHeader: FC<OrganizationsListHeaderProps> = ({
  sortOptions = null,
  onSortChange,
  sortable = true,
}) => {
  const styles = useStyles();

  const onSort = (key: string) => {
    if (!onSortChange) return;
    if (!sortOptions || sortOptions.sortBy !== key) {
      onSortChange({ sortBy: key, direction: "asc" });
      return;
    }
    if (sortOptions.direction === "asc") {
      onSortChange({ sortBy: key, direction: "desc" });
      return;
    }
    onSortChange(null);
  };

  return (
    <Box sx={styles.row}>
      {ORGANIZATIONS_LIST_COLUMNS.map((column) => {
        const isSortable = sortable && column.sortable;

        return (
          <Box
            key={column.key}
            sx={{
              ...styles.cell,
              ...(isSortable && { cursor: "pointer" }),
              width: column.size,
              ...(column.minWidth && { minWidth: column.minWidth }),
              ...(column.align === "center" && { justifyContent: "center" }),
              ...(column.align === "right" && { justifyContent: "flex-end" }),
            }}
            onClick={isSortable ? () => onSort(column.key) : undefined}
          >
            {column.label && (
              <Typography sx={styles.label} noWrap>
                {column.label}
              </Typography>
            )}
            {isSortable && (
              <Box
                sx={{
                  ...styles.sortIcons,
                  opacity: sortOptions?.sortBy === column.key ? 1 : 0.5,
                }}
              >
                <ArrowDropUp
                  sx={
                    sortOptions?.direction === "desc" &&
                    sortOptions?.sortBy === column.key
                      ? { ...styles.sortIcon, opacity: 0.5 }
                      : styles.sortIcon
                  }
                />
                <ArrowDropDown
                  sx={
                    sortOptions?.direction === "asc" &&
                    sortOptions?.sortBy === column.key
                      ? { ...styles.sortIcon, opacity: 0.5 }
                      : styles.sortIcon
                  }
                />
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

const useStyles = makeSxStyles(({ palette: { grey } }) => ({
  row: {
    display: "flex",
    alignItems: "center",
    width: "100%",
  },
  cell: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    padding: "16px",
  },
  label: {
    fontFamily: "Inter",
    fontWeight: 600,
    fontSize: "12px",
    lineHeight: "16px",
    letterSpacing: 0,
    color: grey[100],
  },
  sortIcons: {
    display: "flex",
    flexDirection: "column",
  },
  sortIcon: {
    margin: "-8px 0",
    color: grey[100],
  },
}));

export default OrganizationsListHeader;
